import React, { useState, useMemo, useRef } from 'react';
import Icon from '../../../components/AppIcon';

const emirateDefaults = {
  Dubai: { rentalYield: 6.5, appreciationRate: 8.0, label: 'Dubai', dldFee: 0.04 },
  'Abu Dhabi': { rentalYield: 5.8, appreciationRate: 6.5, label: 'Abu Dhabi', dldFee: 0.02 },
  Sharjah: { rentalYield: 7.2, appreciationRate: 5.0, label: 'Sharjah', dldFee: 0.04 },
  Ajman: { rentalYield: 8.1, appreciationRate: 4.5, label: 'Ajman', dldFee: 0.04 },
  'Ras Al Khaimah': { rentalYield: 7.8, appreciationRate: 7.0, label: 'Ras Al Khaimah', dldFee: 0.04 },
  Fujairah: { rentalYield: 6.9, appreciationRate: 4.0, label: 'Fujairah', dldFee: 0.04 },
  'Umm Al Quwain': { rentalYield: 7.5, appreciationRate: 3.5, label: 'Umm Al Quwain', dldFee: 0.04 },
};

const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

const ROICalculator = () => {
  const [selectedEmirate, setSelectedEmirate] = useState('Dubai');
  const [form, setForm] = useState({
    propertyPrice: 1200000,
    paymentPlan: '20/80',
    downPayment: 20,
    handoverMonths: 24,
    rentalYield: 6.5,
    appreciationRate: 8,
    holdYears: 5
  });
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const calcTimerRef = React.useRef(null);

  const set = (k, v) => {
    const num = parseFloat(v);
    const newErrors = { ...errors };
    if (k === 'propertyPrice') {
      if (isNaN(num) || num <= 0) { newErrors[k] = 'Must be greater than 0'; }
      else if (num > 500000000) { newErrors[k] = 'Maximum AED 500M'; }
      else { delete newErrors?.[k]; }
    }
    if (k === 'rentalYield') {
      if (num < 0) { newErrors[k] = 'Cannot be negative'; }
      else if (num > 20) { newErrors[k] = 'Unusually high — max 20%'; }
      else { delete newErrors?.[k]; }
    }
    if (k === 'appreciationRate') {
      if (num < -10) { newErrors[k] = 'Min -10% (depreciation)'; }
      else if (num > 30) { newErrors[k] = 'Max 30%'; }
      else { delete newErrors?.[k]; }
    }
    setErrors(newErrors);
    setForm(prev => ({ ...prev, [k]: isNaN(num) ? v : num }));
    // Show brief calculating state
    setIsCalculating(true);
    if (calcTimerRef?.current) clearTimeout(calcTimerRef?.current);
    calcTimerRef.current = setTimeout(() => setIsCalculating(false), 400);
  };

  const handleEmirateChange = (emirate) => {
    setSelectedEmirate(emirate);
    const defaults = emirateDefaults?.[emirate];
    setForm(prev => ({
      ...prev,
      rentalYield: defaults?.rentalYield,
      appreciationRate: defaults?.appreciationRate,
    }));
    setErrors({});
    setIsCalculating(true);
    if (calcTimerRef?.current) clearTimeout(calcTimerRef?.current);
    calcTimerRef.current = setTimeout(() => setIsCalculating(false), 500);
  };

  const calc = useMemo(() => {
    const price = Math.max(0, form?.propertyPrice || 0);
    const dpPct = clamp(form?.downPayment || 0, 0, 100);
    const emirateData = emirateDefaults?.[selectedEmirate];
    const dp = price * (dpPct / 100);
    const dldFee = price * emirateData?.dldFee;
    const adminFee = price > 500000 ? 4000 : 2000;
    const agencyFee = price * 0.02;
    const totalUpfront = dp + dldFee + adminFee + agencyFee;
    const rentalYield = Math.max(0, form?.rentalYield || 0);
    const appreciationRate = form?.appreciationRate || 0;
    const holdYears = Math.max(1, form?.holdYears || 1);
    const annualRent = price * (rentalYield / 100);
    const totalRent = annualRent * holdYears;
    const futureValue = price * Math.pow(1 + appreciationRate / 100, holdYears);
    const capitalGain = futureValue - price;
    const totalReturn = totalRent + capitalGain;
    const roi = totalUpfront > 0 ? ((totalReturn / totalUpfront) * 100)?.toFixed(1) : '0.0';
    const annualROI = (parseFloat(roi) / holdYears)?.toFixed(1);
    const isValid = price > 0 && Object.keys(errors)?.length === 0;
    return { dp, dldFee, adminFee, agencyFee, totalUpfront, annualRent, totalRent, futureValue, capitalGain, totalReturn, roi, annualROI, isValid, emirateData };
  }, [form, selectedEmirate, errors]);

  const fmt = (n) => 'AED ' + Math.round(Math.max(0, n))?.toLocaleString();
  const roiColor = parseFloat(calc?.roi) >= 50 ? '#00FF88' : parseFloat(calc?.roi) >= 20 ? '#00D4FF' : parseFloat(calc?.roi) >= 0 ? '#FFC800' : '#FF6464';

  return (
    <div className="rounded-2xl p-6 md:p-8" style={{ background: 'rgba(10,22,40,0.9)', border: '1px solid rgba(0,212,255,0.25)' }}>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.3)' }}>
          <Icon name="Calculator" size={20} color="#00D4FF" />
        </div>
        <div>
          <h2 className="text-lg md:text-xl font-bold" style={{ color: '#E8F4FF' }}>UAE Off-Plan ROI Calculator</h2>
          <p className="text-xs" style={{ color: '#6B9EC4' }}>AED-based investment analysis with emirate-specific fees and market data</p>
        </div>
      </div>
      {/* Emirate Selector */}
      <div className="mb-5">
        <label className="text-xs font-medium mb-2 block" style={{ color: '#6B9EC4' }}>Select Emirate</label>
        <div className="flex flex-wrap gap-2">
          {Object.keys(emirateDefaults)?.map(emirate => (
            <button
              key={emirate}
              onClick={() => handleEmirateChange(emirate)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200"
              style={selectedEmirate === emirate
                ? { background: 'rgba(0,212,255,0.2)', border: '1px solid rgba(0,212,255,0.6)', color: '#00D4FF' }
                : { background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.15)', color: '#6B9EC4' }
              }
            >
              {emirate}
            </button>
          ))}
        </div>
        <div className="mt-2 flex gap-4">
          <span className="text-xs" style={{ color: '#4A7A9B' }}>Avg Rental Yield: <span style={{ color: '#00D4FF' }}>{emirateDefaults?.[selectedEmirate]?.rentalYield}%</span></span>
          <span className="text-xs" style={{ color: '#4A7A9B' }}>Avg Appreciation: <span style={{ color: '#00D4FF' }}>{emirateDefaults?.[selectedEmirate]?.appreciationRate}%</span></span>
          <span className="text-xs" style={{ color: '#4A7A9B' }}>Transfer Fee: <span style={{ color: '#00D4FF' }}>{(emirateDefaults?.[selectedEmirate]?.dldFee * 100)?.toFixed(0)}%</span></span>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Inputs */}
        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium mb-1 block" style={{ color: '#6B9EC4' }}>Property Price (AED)</label>
            <input
              type="number"
              value={form?.propertyPrice}
              onChange={e => set('propertyPrice', e?.target?.value)}
              min={0}
              className="w-full px-4 py-2.5 rounded-xl text-sm outline-none"
              style={{ background: 'rgba(0,212,255,0.05)', border: `1px solid ${errors?.propertyPrice ? '#FF6464' : 'rgba(0,212,255,0.2)'}`, color: '#E8F4FF' }}
            />
            {errors?.propertyPrice && <p className="text-xs mt-1" style={{ color: '#FF6464' }}>{errors?.propertyPrice}</p>}
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block" style={{ color: '#6B9EC4' }}>Payment Plan</label>
            <select
              value={form?.paymentPlan}
              onChange={e => { set('paymentPlan', e?.target?.value); set('downPayment', parseInt(e?.target?.value)); }}
              className="w-full px-4 py-2.5 rounded-xl text-sm outline-none"
              style={{ background: 'rgba(10,22,40,0.9)', border: '1px solid rgba(0,212,255,0.2)', color: '#E8F4FF' }}
            >
              <option value="10/90">10/90 — 10% now, 90% on handover</option>
              <option value="20/80">20/80 — 20% now, 80% on handover</option>
              <option value="30/70">30/70 — 30% now, 70% on handover</option>
              <option value="50/50">50/50 — Equal split during construction</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium mb-2 block" style={{ color: '#6B9EC4' }}>Handover Timeline: <span style={{ color: '#00D4FF' }}>{form?.handoverMonths} months</span></label>
            <input type="range" min={12} max={60} value={form?.handoverMonths} onChange={e => set('handoverMonths', +e?.target?.value)} className="w-full" />
          </div>
          <div>
            <label className="text-xs font-medium mb-2 block" style={{ color: '#6B9EC4' }}>Expected Rental Yield: <span style={{ color: '#00D4FF' }}>{form?.rentalYield}%</span></label>
            <input type="range" min={0} max={20} step={0.5} value={form?.rentalYield} onChange={e => set('rentalYield', +e?.target?.value)} className="w-full" />
            {errors?.rentalYield && <p className="text-xs mt-1" style={{ color: '#FF6464' }}>{errors?.rentalYield}</p>}
            <div className="flex justify-between text-xs mt-1" style={{ color: '#4A7A9B' }}>
              <span>0%</span><span>Market avg: {emirateDefaults?.[selectedEmirate]?.rentalYield}%</span><span>20%</span>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium mb-2 block" style={{ color: '#6B9EC4' }}>Annual Appreciation: <span style={{ color: '#00D4FF' }}>{form?.appreciationRate}%</span></label>
            <input type="range" min={-10} max={30} step={0.5} value={form?.appreciationRate} onChange={e => set('appreciationRate', +e?.target?.value)} className="w-full" />
            {errors?.appreciationRate && <p className="text-xs mt-1" style={{ color: '#FF6464' }}>{errors?.appreciationRate}</p>}
            <div className="flex justify-between text-xs mt-1" style={{ color: '#4A7A9B' }}>
              <span>-10%</span><span>Market avg: {emirateDefaults?.[selectedEmirate]?.appreciationRate}%</span><span>30%</span>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium mb-2 block" style={{ color: '#6B9EC4' }}>Hold Period: <span style={{ color: '#00D4FF' }}>{form?.holdYears} years</span></label>
            <input type="range" min={1} max={15} value={form?.holdYears} onChange={e => set('holdYears', +e?.target?.value)} className="w-full" />
          </div>
        </div>

        {/* Results */}
        <div className="space-y-3">
          {!calc?.isValid && (
            <div className="p-3 rounded-xl" style={{ background: 'rgba(255,100,100,0.08)', border: '1px solid rgba(255,100,100,0.3)' }}>
              <div className="flex items-center gap-2">
                <Icon name="AlertCircle" size={14} color="#FF6464" />
                <span className="text-xs" style={{ color: '#FF6464' }}>Please fix input errors to see accurate results</span>
              </div>
            </div>
          )}

          {/* ROI result — spinner overlay while calculating */}
          <div className="relative">
            {isCalculating && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl" style={{ background: 'rgba(10,22,40,0.7)', backdropFilter: 'blur(2px)' }}>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#00D4FF', borderTopColor: 'transparent' }} />
                  <span className="text-xs font-medium" style={{ color: '#00D4FF' }}>Calculating...</span>
                </div>
              </div>
            )}
            <div className="p-4 rounded-xl text-center" style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.3)' }}>
              <div className="text-3xl font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: roiColor }}>{calc?.roi}%</div>
              <div className="text-xs" style={{ color: '#6B9EC4' }}>Total ROI over {form?.holdYears} years</div>
              <div className="text-sm font-semibold mt-1" style={{ color: roiColor }}>{calc?.annualROI}% per year</div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-xs font-semibold mb-2" style={{ color: '#6B9EC4' }}>UPFRONT COSTS</div>
            {[
              { label: 'Down Payment', value: fmt(calc?.dp) },
              { label: `Transfer Fee (${(calc?.emirateData?.dldFee * 100)?.toFixed(0)}%)`, value: fmt(calc?.dldFee) },
              { label: 'Admin Fee', value: fmt(calc?.adminFee) },
              { label: 'Agency Fee (2%)', value: fmt(calc?.agencyFee) },
              { label: 'Total Upfront', value: fmt(calc?.totalUpfront), highlight: true }
            ]?.map((item, i) => (
              <div key={i} className="flex justify-between text-sm py-1.5 px-3 rounded-lg" style={{ background: item?.highlight ? 'rgba(0,212,255,0.1)' : 'rgba(0,212,255,0.04)', border: item?.highlight ? '1px solid rgba(0,212,255,0.3)' : 'none' }}>
                <span style={{ color: '#6B9EC4' }}>{item?.label}</span>
                <span style={{ color: item?.highlight ? '#00D4FF' : '#E8F4FF', fontWeight: item?.highlight ? 700 : 400 }}>{item?.value}</span>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <div className="text-xs font-semibold mb-2" style={{ color: '#6B9EC4' }}>RETURNS</div>
            {[
              { label: `Rental Income (${form?.holdYears}yr)`, value: fmt(calc?.totalRent) },
              { label: 'Capital Appreciation', value: fmt(calc?.capitalGain), negative: calc?.capitalGain < 0 },
              { label: 'Total Return', value: fmt(calc?.totalReturn), highlight: true, negative: calc?.totalReturn < 0 }
            ]?.map((item, i) => (
              <div key={i} className="flex justify-between text-sm py-1.5 px-3 rounded-lg" style={{ background: item?.highlight ? (item?.negative ? 'rgba(255,100,100,0.1)' : 'rgba(34,197,94,0.1)') : 'rgba(0,212,255,0.04)', border: item?.highlight ? (item?.negative ? '1px solid rgba(255,100,100,0.3)' : '1px solid rgba(34,197,94,0.3)') : 'none' }}>
                <span style={{ color: '#6B9EC4' }}>{item?.label}</span>
                <span style={{ color: item?.highlight ? (item?.negative ? '#FF6464' : '#22c55e') : (item?.negative ? '#FF6464' : '#E8F4FF'), fontWeight: item?.highlight ? 700 : 400 }}>{item?.value}</span>
              </div>
            ))}
          </div>

          {/* Emirate Market Benchmarks */}
          <div className="p-3 rounded-xl" style={{ background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.15)' }}>
            <div className="text-xs font-semibold mb-2" style={{ color: '#6B9EC4' }}>{selectedEmirate?.toUpperCase()} MARKET BENCHMARKS</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Avg Yield', value: `${emirateDefaults?.[selectedEmirate]?.rentalYield}%` },
                { label: 'Avg Appreciation', value: `${emirateDefaults?.[selectedEmirate]?.appreciationRate}%` },
                { label: 'Transfer Fee', value: `${(emirateDefaults?.[selectedEmirate]?.dldFee * 100)?.toFixed(0)}%` },
              ]?.map((b, i) => (
                <div key={i} className="text-center">
                  <div className="text-sm font-bold" style={{ color: '#00D4FF' }}>{b?.value}</div>
                  <div className="text-xs" style={{ color: '#4A7A9B' }}>{b?.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ROICalculator;
